import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pluggyPendenciasApi, categoriasApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Select } from '../../components/ui/Input'
import { useToast } from '../../components/ui/Toast'
import { formatMoney, formatDate } from '../../lib/utils'
import type { Categoria, FaturaAbertaConta, PluggyPendenciaInfo } from '../../types'

function rotuloFatura(f: FaturaAbertaConta) {
  return `${f.cartaoNome} · ${String(f.mes).padStart(2, '0')}/${f.ano} · vence ${formatDate(f.vencimento)} · ${formatMoney(f.total)}`
}

function achatar(categorias: Categoria[]): Categoria[] {
  return categorias.flatMap((c) => [c, ...achatar(c.subCategorias ?? [])])
}

/**
 * Pendência tipo 5: débito na conta que parece pagamento de fatura de cartão.
 * Nunca vira despesa sozinho: a pessoa vincula ao que já existe, paga a fatura com ele, registra como parcial ou diz que é outra coisa.
 */
export function PagamentoContaSituacao({ p }: { p: PluggyPendenciaInfo }) {
  const qc = useQueryClient()
  const toast = useToast()
  const candidatos = p.pagamentoCandidatos ?? []
  const abertas = p.faturasAbertasConta ?? []
  const sugerida = abertas.find((f) => f.sugerida)?.faturaId ?? abertas[0]?.faturaId ?? ''
  const [faturaId, setFaturaId] = useState('')
  const [outraAberta, setOutraAberta] = useState(false)
  const [categoriaId, setCategoriaId] = useState('')
  const escolhida = abertas.find((f) => f.faturaId === (faturaId || sugerida))

  const { data: categorias = [] } = useQuery({
    queryKey: ['categorias'], queryFn: () => categoriasApi.list({ incluirInativas: false }), enabled: outraAberta,
  })

  function depois() {
    qc.invalidateQueries({ queryKey: ['pluggy-pendencias'] })
    qc.invalidateQueries({ queryKey: ['pluggy-historico'] })
    qc.invalidateQueries({ queryKey: ['transacoes'] })
    qc.invalidateQueries({ queryKey: ['contas'] })
    qc.invalidateQueries({ queryKey: ['cartoes'] })
    qc.invalidateQueries({ queryKey: ['fatura'] })
    qc.invalidateQueries({ queryKey: ['fatura-categorias'] })
    qc.invalidateQueries({ queryKey: ['faturas-resumo'] })
    qc.invalidateQueries({ queryKey: ['dashboard'] })
  }
  // 404 (já resolvida / fatura de fora) e 409 (já paga / já ligada): mostra a mensagem do servidor e atualiza a lista.
  const comum = (msg: string) => ({
    onSuccess: () => { toast.success(msg); depois() },
    onError: (e: Error) => { toast.error(e.message); depois() },
  })

  const vincular = useMutation({
    mutationFn: (alvo: { faturaId?: string; ajusteId?: string }) => pluggyPendenciasApi.vincularPagamentoConta(p.id, alvo),
    ...comum('Débito vinculado ao pagamento da fatura.'),
  })
  const pagar = useMutation({
    mutationFn: (id: string) => pluggyPendenciasApi.pagarPagamentoConta(p.id, id),
    ...comum('Fatura paga com este débito.'),
  })
  const parcial = useMutation({
    mutationFn: (id: string) => pluggyPendenciasApi.parcialPagamentoConta(p.id, id),
    ...comum('Pagamento parcial registrado na fatura.'),
  })
  const outra = useMutation({
    mutationFn: () => pluggyPendenciasApi.outraCoisaPagamentoConta(p.id, categoriaId || undefined),
    ...comum('Lançado como despesa comum.'),
  })
  const ocupado = vincular.isPending || pagar.isPending || parcial.isPending || outra.isPending

  return (
    <div className="space-y-2">
      <Badge variant="info" className="normal-case">Pagamento de fatura (débito na conta)</Badge>

      {candidatos.length > 0 ? (
        <div className="space-y-2">
          {candidatos.map((c) => (
            <div key={`${c.tipo}-${c.id}`} className="flex flex-wrap items-center gap-2">
              <p className="text-13 text-white">
                Parece o pagamento {c.tipo === 'ajuste' ? 'do ajuste' : 'da fatura'} {c.rotulo}
                <span className="block text-2xs text-muted">{c.cartaoNome}</span>
              </p>
              <Button size="sm" variant="soft-primary" disabled={ocupado} loading={vincular.isPending}
                onClick={() => vincular.mutate(c.tipo === 'ajuste' ? { ajusteId: c.id } : { faturaId: c.id })}>Vincular</Button>
            </div>
          ))}
          <p className="text-2xs text-muted">O débito só é ligado ao que já existe: a conta não é debitada de novo e nada vira gasto.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {abertas.length > 0 ? (
            <div className="space-y-2">
              <Select aria-label="Fatura" value={faturaId || sugerida} onChange={(e) => setFaturaId(e.target.value)}>
                {abertas.map((f) => <option key={f.faturaId} value={f.faturaId}>{rotuloFatura(f)}{f.sugerida ? ' (sugerida)' : ''}</option>)}
              </Select>
              <div>
                <Button size="sm" variant="soft-primary" disabled={ocupado || !escolhida} loading={pagar.isPending}
                  onClick={() => escolhida && window.confirm(`Marcar a fatura ${rotuloFatura(escolhida)} como paga em ${formatDate(p.data)}?`) && pagar.mutate(escolhida.faturaId)}>
                  Pagar a fatura{escolhida ? ` ${String(escolhida.mes).padStart(2, '0')}/${escolhida.ano}` : ''} com este débito
                </Button>
                <p className="mt-0.5 text-2xs text-muted">A fatura será marcada como paga e a conta debitada uma vez só.</p>
              </div>
              <div>
                <Button size="sm" variant="light" disabled={ocupado || !escolhida} loading={parcial.isPending}
                  onClick={() => escolhida && parcial.mutate(escolhida.faturaId)}>
                  Pagamento parcial da fatura{escolhida ? ` ${String(escolhida.mes).padStart(2, '0')}/${escolhida.ano}` : ''}
                </Button>
                <p className="mt-0.5 text-2xs text-muted">O débito vira ajuste que reduz a fatura; a sobra segue como saldo transferido na próxima importação.</p>
              </div>
            </div>
          ) : (
            <p className="text-13 text-muted">Nenhuma fatura em aberto dos cartões pagos por esta conta.</p>
          )}

          {outraAberta ? (
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-48">
                <Select aria-label="Categoria (opcional)" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
                  <option value="">Sem categoria</option>
                  {achatar(categorias).map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
                </Select>
              </div>
              <Button size="sm" variant="soft-primary" disabled={ocupado} loading={outra.isPending} onClick={() => outra.mutate()}>Lançar como despesa</Button>
              <Button size="sm" variant="ghost" disabled={ocupado} onClick={() => setOutraAberta(false)}>Voltar</Button>
            </div>
          ) : (
            <Button size="sm" variant="light" disabled={ocupado} onClick={() => setOutraAberta(true)}>É outra coisa</Button>
          )}
        </div>
      )}
    </div>
  )
}
